import { Fragment, useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetETAAccuracy } from 'api/kpi/kpi';
import { useExportExportETAAccuracyHook } from 'api/export/export';
import { KPIETAAccuracyRow } from 'api/model';
import useTranslations from 'contexts/basicData/useTranslations';
import { maxKPIDateRangeMs } from 'constants/AppConstants';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
} from './management/shared';
import useExpandedRows from './operations/useExpandedRows';
import localityLabel from './operations/locality';

const StatBox = ({
  label,
  value,
  count,
  color,
}: {
  label: string;
  value: string;
  count: number;
  color: string;
}) => (
  <div
    style={{
      textAlign: 'center',
      padding: '15px',
      backgroundColor: `${color}15`,
      borderRadius: '8px',
      border: `2px solid ${color}`,
    }}
  >
    <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
      ({count} st)
    </div>
  </div>
);

const minutes = (value: number) => `${value > 0 ? '+' : ''}${nf(value, 1)} min`;

/** De gemensamma kolumnerna efter radens etikett. */
const RowCells = ({ row }: { row: KPIETAAccuracyRow }) => (
  <>
    <td style={tdNumStyle}>{nf(row.total)}</td>
    <td style={tdNumStyle}>{pct(row.onTimePercentage)}</td>
    <td style={tdNumStyle}>{pct(row.latePercentage)}</td>
    <td style={tdNumStyle}>{pct(row.earlyPercentage)}</td>
    <td style={tdNumStyle}>{minutes(row.medianDeviationMinutes)}</td>
  </>
);

const RowHeaders = ({ first }: { first: string }) => (
  <tr>
    <th style={thStyle}>{first}</th>
    <th style={thNumStyle}>Antal</th>
    <th style={thNumStyle}>I tid</th>
    <th style={thNumStyle}>Sena</th>
    <th style={thNumStyle}>Tidiga</th>
    <th style={thNumStyle}>Median</th>
  </tr>
);

/**
 * Ankomstprecision mot bokad tid på kundbenet: hemleverans mäts på GPS-ankomsten till kunden,
 * inköp, auktion och inbyte på FT:s "Anlänt" hos kunden. Per kundens län, med uppdelning på om
 * FT hade hemankare i länet. Se KPILogic.GetETAAccuracy.
 */
const ETAAccuracyWidget = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const expanded = useExpandedRows();
  const { caseTypes } = useTranslations();

  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getETAAccuracyData = useKPIGetETAAccuracy();

  const startDate = daterange[0];
  const endDate = daterange[1];

  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  const isDateRangeTooLong =
    startDate &&
    endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  useEffect(() => {
    if (startDate && endDate && !isDateRangeTooLong) {
      getETAAccuracyData.mutate({
        data: {
          startDate,
          endDate,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
    excludeCompanyNameContains,
    isDateRangeTooLong,
  ]);

  const downloadExcel = useExportExportETAAccuracyHook();

  const { data } = getETAAccuracyData;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.ETAAccuracy]}
      count={data?.totalAssignmentsWithETA}
      isLoading={getETAAccuracyData.isLoading}
      info={
        'Ankomst hos kunden jämfört med bokad tid. Hemleverans: GPS-ankomst till kunden. Inköp, auktion och inbyte: fälttestarens "Anlänt" hos kunden. I tid = ±5 min. ' +
        'Län = kundens län (till-län på hemleverans, från-län annars). "FT kopplad till länet" = FT har ett hemankare i länet. ' +
        'Kundben utan ankomsttid kan inte mätas och räknas inte. Klicka på ett län för att se uppdelningen.'
      }
    >
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={isDownloading || !startDate || !endDate}
          onClick={async () => {
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                startDate: daterange[0]!,
                endDate: daterange[1]!,
                supplierIDs: selectedSupplierIDs,
                customerNames: selectedCustomerNames,
                fromCountyIDs: selectedFromCountyIDs,
                toCountyIDs: selectedToCountyIDs,
                excludeCompanyNameContains:
                  excludeCompanyNameContains ?? undefined,
              });
              if (result) {
                downloadBlob(
                  result,
                  `Nyckeltal_ETAPrecision_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      {isDateRangeTooLong ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>
            Datumintervallet är för långt för att visa data. Använd
            nedladdningsknappen för att exportera data.
          </p>
        </div>
      ) : !data || data.totalAssignmentsWithETA === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getETAAccuracyData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen ankomstdata att visa för vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ padding: '10px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '15px',
              marginBottom: '20px',
            }}
          >
            <StatBox
              label="I tid"
              value={`${data.onTimePercentage}%`}
              count={data.onTimeCount}
              color="#28a745"
            />
            <StatBox
              label="Sena"
              value={`${data.latePercentage}%`}
              count={data.lateCount}
              color="#dc3545"
            />
            <StatBox
              label="Tidiga"
              value={`${data.earlyPercentage}%`}
              count={data.earlyCount}
              color="#17a2b8"
            />
          </div>

          <p style={{ fontSize: '14px', margin: '0 0 12px' }}>
            Median <strong>{minutes(data.medianDeviationMinutes)}</strong> ·
            snitt {minutes(data.averageDeviationMinutes)} mot bokad tid
          </p>

          <table style={tableStyle}>
            <thead>
              <RowHeaders first="Ärendetyp" />
            </thead>
            <tbody>
              {data.byCaseType.map((row) => (
                <tr
                  key={row.caseType ?? 'none'}
                  style={{ borderBottom: '1px solid #eee' }}
                >
                  <td style={tdStyle}>
                    {row.caseType != null ? caseTypes[row.caseType] : '–'}
                  </td>
                  <RowCells row={row} />
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ ...tableStyle, marginTop: '16px' }}>
            <thead>
              <RowHeaders first="Kundens län" />
            </thead>
            <tbody>
              {data.byCounty.map((county) => {
                const isOpen = expanded.isOpen(county.countyName);
                return (
                  <Fragment key={county.countyName}>
                    <tr
                      style={{
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                      }}
                      onClick={() => expanded.toggle(county.countyName)}
                    >
                      <td style={tdStyle}>
                        {isOpen ? '▾' : '▸'} {county.countyName}{' '}
                        <span style={{ color: '#666', fontSize: '12px' }}>
                          ({pct(county.localPercentage)} lokal FT)
                        </span>
                      </td>
                      <RowCells row={county} />
                    </tr>
                    {isOpen &&
                      county.byLocality.map((row) => (
                        <tr
                          key={`${county.countyName}-${row.locality}`}
                          style={{
                            borderBottom: '1px solid #f3f3f3',
                            color: '#555',
                          }}
                        >
                          <td style={{ ...tdStyle, paddingLeft: '28px' }}>
                            {row.locality != null
                              ? localityLabel[row.locality]
                              : '–'}
                          </td>
                          <RowCells row={row} />
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <table style={{ ...tableStyle, marginTop: '16px' }}>
            <thead>
              <RowHeaders first="Alla län" />
            </thead>
            <tbody>
              {data.byLocality.map((row) => (
                <tr
                  key={row.locality ?? 'none'}
                  style={{ borderBottom: '1px solid #eee' }}
                >
                  <td style={tdStyle}>
                    {row.locality != null ? localityLabel[row.locality] : '–'}
                  </td>
                  <RowCells row={row} />
                </tr>
              ))}
            </tbody>
          </table>

          <ControlFigures
            items={[
              data.legsWithoutArrival
                ? `${nf(data.legsWithoutArrival)} kundben utan ankomsttid räknas inte`
                : null,
            ]}
          />
        </div>
      )}
    </Widget>
  );
};

export default ETAAccuracyWidget;
